import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useGame } from "../../context/GameContext.jsx";
import { useLanguage } from "../../hooks/useLanguage.js";
import { Panel } from "../ui/Panel.jsx";
import { ROLE_DATA } from "../../data/roles.js";
import { DEFAULT_NIGHT_ROLE_ORDER } from "../../data/constants.js";

// ── Night Role Order drag section ────────────────────────────────
function NightRoleOrderSection({ s, dispatch, t }) {
  const [open, setOpen] = useState(false);
  const currentOrder = s.nightRoleOrder || DEFAULT_NIGHT_ROLE_ORDER;
  const isDefault = JSON.stringify(currentOrder) === JSON.stringify(DEFAULT_NIGHT_ROLE_ORDER);

  const allRoles = currentOrder.filter(id => ROLE_DATA[id]);
  const missing  = DEFAULT_NIGHT_ROLE_ORDER.filter(id => !currentOrder.includes(id) && ROLE_DATA[id]);
  const displayOrder = [...allRoles, ...missing];

  const [dragId, setDragId] = useState(null);
  const [localOrder, setLocalOrder] = useState(displayOrder);
  useEffect(() => { setLocalOrder(displayOrder); }, [s.nightRoleOrder]);

  const onDragStart = (id) => setDragId(id);
  const onDragOver = (e, overId) => {
    e.preventDefault();
    if (overId === dragId) return;
    const from = localOrder.indexOf(dragId);
    const to   = localOrder.indexOf(overId);
    if (from === -1 || to === -1) return;
    const next = [...localOrder];
    next.splice(to, 0, next.splice(from, 1)[0]);
    setLocalOrder(next);
  };
  const onDrop = () => {
    dispatch({ type: "SET_SETTING", payload: { key: "nightRoleOrder", value: localOrder } });
    setDragId(null);
  };
  const reset = () => {
    setLocalOrder(DEFAULT_NIGHT_ROLE_ORDER.filter(id => ROLE_DATA[id]));
    dispatch({ type: "SET_SETTING", payload: { key: "nightRoleOrder", value: null } });
  };

  return (
    <div className="mt-4 border-t border-amber-900/20 pt-4">
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-2 active:scale-98">
        <div className="flex items-center gap-2">
          <span className="text-amber-100 text-sm font-semibold">
            🌙 {t("Ordre des rôles la nuit","Night Role Order")}
          </span>
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold
            ${isDefault
              ? "bg-emerald-950 border border-emerald-800/40 text-emerald-500"
              : "bg-amber-950 border border-amber-700/40 text-amber-400"}`}>
            {isDefault ? t("Défaut","Default") : t("Personnalisé","Custom")}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {!isDefault && (
            <button onClick={(e) => { e.stopPropagation(); reset(); }}
              className="text-[10px] px-2 py-0.5 rounded-lg bg-stone-900 border border-stone-700
                text-stone-400 active:scale-95">
              {t("Réinitialiser","Reset")}
            </button>
          )}
          <span className="text-amber-700 text-sm">{open ? "▲" : "▼"}</span>
        </div>
      </button>

      {open && (
        <motion.div
          initial={{ opacity:0, height:0 }} animate={{ opacity:1, height:"auto" }}
          exit={{ opacity:0, height:0 }} className="mt-3 overflow-hidden">
          <p className="text-amber-700/50 text-[10px] mb-3 italic">
            {t("Faites glisser pour réordonner. Affecte uniquement l'ordre de réveil, pas la résolution.",
               "Drag to reorder. Affects wake-up order only, not resolution.")}
          </p>
          <div className="flex flex-col gap-1.5">
            {localOrder.map(roleId => {
              const role = ROLE_DATA[roleId];
              if (!role) return null;
              const isPassive = role.activationTiming === "passive" || role.activationTiming === "on_death";
              return (
                <div key={roleId}
                  draggable
                  onDragStart={() => onDragStart(roleId)}
                  onDragOver={(e) => onDragOver(e, roleId)}
                  onDrop={onDrop}
                  className={`flex items-center gap-2 rounded-xl px-3 py-2
                    bg-stone-950/60 border border-stone-800/30 transition-opacity
                    ${dragId === roleId ? "opacity-40" : ""} ${isPassive ? "opacity-50" : ""}`}>
                  <span className="text-amber-800/60 cursor-grab active:cursor-grabbing select-none px-0.5">≡</span>
                  <span className="text-base">{role.emoji}</span>
                  <span className="flex-1 text-amber-100/80 text-xs">{role.frenchName}</span>
                  {isPassive && <span className="text-[9px] text-stone-600 italic">{t("passif","passive")}</span>}
                </div>
              );
            })}
          </div>
        </motion.div>
      )}
    </div>
  );
}

// ── Main SettingsSheet ────────────────────────────────────────────
export function SettingsSheet({ onClose }) {
  const { state, dispatch } = useGame();
  const { t } = useLanguage();
  const s = state.settings;

  const toggle = (key) =>
    dispatch({ type: "SET_SETTING", payload: { key, value: !s[key] } });

  const Row = ({ label, sublabel, settingKey }) => (
    <div className="flex items-center justify-between gap-3 py-3
      border-b border-amber-900/20 last:border-0">
      <div className="flex-1">
        <p className="text-amber-100 text-sm font-semibold">{label}</p>
        {sublabel && <p className="text-amber-700/60 text-xs mt-0.5">{sublabel}</p>}
      </div>
      <button onClick={() => toggle(settingKey)}
        className={`w-12 h-6 rounded-full border transition-all shrink-0 relative
          ${s[settingKey] ? "bg-amber-500 border-amber-400" : "bg-stone-800 border-stone-700"}`}>
        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all
          ${s[settingKey] ? "left-6" : "left-0.5"}`} />
      </button>
    </div>
  );

  return (
    <motion.div
      initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      className="fixed inset-0 z-50 flex flex-col justify-end"
      style={{ background:"rgba(0,0,0,0.7)" }}
      onClick={onClose}>
      <motion.div
        initial={{ y:"100%" }} animate={{ y:0 }} exit={{ y:"100%" }}
        transition={{ type:"spring", stiffness:300, damping:30 }}
        onClick={e => e.stopPropagation()}
        className="bg-stone-950 border-t border-amber-900/30 rounded-t-3xl px-4 pt-4 pb-10 max-h-[80vh] overflow-y-auto">

        <div className="w-10 h-1 bg-amber-900/40 rounded-full mx-auto mb-4" />
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-amber-200 font-bold text-base">⚙️ {t("Réglages","Settings")}</h2>
          <button onClick={onClose}
            className="text-amber-700 w-8 h-8 flex items-center justify-center rounded-xl
              bg-amber-950/50 active:scale-95">✕</button>
        </div>

        <p className="text-amber-700/50 text-xs uppercase tracking-widest mb-3">
          {t("Déroulement de la partie","Game flow")}
        </p>

        <Panel>
          <Row settingKey="mayorEnabled"
            label={t("👑 Maire activé","👑 Mayor enabled")}
            sublabel={t("Désactivé : la partie commence directement la nuit.",
                        "Disabled: game starts directly at night.")} />
          <Row settingKey="witchPoisonOnNight1"
            label={t("☠️ Poison Sorcière — Nuit 1","☠️ Witch poison — Night 1")}
            sublabel={t("Désactivé (défaut) : la potion de mort est bloquée lors de la première nuit.",
                        "Disabled (default): death potion is blocked on the first night.")} />
          <Row settingKey="fatherInfectionOnNight1"
            label={t("🦠 Infection Père Infect — Nuit 1","🦠 Father infection — Night 1")}
            sublabel={t("Désactivé (défaut) : l'infection est bloquée lors de la première nuit.",
                        "Disabled (default): infection is blocked on Night 1.")} />
          <Row settingKey="muteSeer"
            label={t("🔮 Voyante aveugle","🔮 Blind Seer")}
            sublabel={t("Activé : la Voyante voit seulement le camp, pas le rôle exact.",
                        "Enabled: the Seer sees only the camp, not the exact role.")} />
          <Row settingKey="flutePlayerConstantCount"
            label={t("🪗 Joueur de Flûte — nombre fixe","🪗 Flute Player — fixed count")}
            sublabel={t("Désactivé : alterne 2 / 1 / 2 / 1…","Disabled: alternates 2 / 1 / 2 / 1…")} />

          {/* Flute charms per night — always shown, greyed when constant mode off */}
          <div className={`py-3 border-b border-amber-900/20 last:border-0 transition-opacity ${!s.flutePlayerConstantCount ? "opacity-40" : ""}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1">
                <p className={`text-sm font-semibold ${s.flutePlayerConstantCount ? "text-amber-100" : "text-amber-700"}`}>
                  {t("Joueurs ensorcelés par nuit","Players charmed per night")}
                </p>
                <p className="text-amber-700/60 text-xs mt-0.5">
                  {s.flutePlayerConstantCount ? t("Minimum 1","Minimum 1") : t("Inactif (mode alterné)","Inactive (alternating mode)")}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button disabled={!s.flutePlayerConstantCount}
                  onClick={() => s.flutePlayerConstantCount && dispatch({ type:"SET_SETTING", payload:{ key:"flutePlayerCharmsPerNight", value: Math.max(1,(s.flutePlayerCharmsPerNight??2)-1) } })}
                  className="w-8 h-8 rounded-full bg-stone-800 border border-stone-700 text-amber-400 flex items-center justify-center active:scale-95 disabled:opacity-30">−</button>
                <span className="w-6 text-center text-amber-200 font-bold">{s.flutePlayerCharmsPerNight ?? 2}</span>
                <button disabled={!s.flutePlayerConstantCount}
                  onClick={() => s.flutePlayerConstantCount && dispatch({ type:"SET_SETTING", payload:{ key:"flutePlayerCharmsPerNight", value:(s.flutePlayerCharmsPerNight??2)+1 } })}
                  className="w-8 h-8 rounded-full bg-amber-900/60 border border-amber-700/60 text-amber-300 flex items-center justify-center active:scale-95 disabled:opacity-30">+</button>
              </div>
            </div>
          </div>

          {/* Shepherd sheep count */}
          <div className="py-3 border-b border-amber-900/20 last:border-0">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1">
                <p className="text-amber-100 text-sm font-semibold">
                  🐑 {t("Nombre de moutons du Berger","Number of sheep for the Shepherd")}
                </p>
                <p className="text-amber-700/60 text-xs mt-0.5">{t("Défaut : 3","Default: 3")}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => dispatch({ type:"SET_SETTING", payload:{ key:"shepherdSheepCount", value: Math.max(0,(s.shepherdSheepCount??3)-1) } })}
                  className="w-8 h-8 rounded-full bg-stone-800 border border-stone-700 text-amber-400 flex items-center justify-center active:scale-95">−</button>
                <span className="w-6 text-center text-amber-200 font-bold">{s.shepherdSheepCount ?? 3}</span>
                <button
                  onClick={() => dispatch({ type:"SET_SETTING", payload:{ key:"shepherdSheepCount", value:(s.shepherdSheepCount??3)+1 } })}
                  className="w-8 h-8 rounded-full bg-amber-900/60 border border-amber-700/60 text-amber-300 flex items-center justify-center active:scale-95">+</button>
              </div>
            </div>
          </div>
        </Panel>

        <NightRoleOrderSection s={s} dispatch={dispatch} t={t} />

        <p className="text-amber-700/40 text-[10px] text-center mt-4 italic">
          {t("Les réglages sont sauvegardés automatiquement.","Settings are saved automatically.")}
        </p>
      </motion.div>
    </motion.div>
  );
}
